import styled, { keyframes } from 'styled-components';

export const Container = styled.div`
  width: 100%;
  height: 100%;
  background: #000000;
  overflow: hidden;
  position: relative;
`;

export const ContentViewport = styled.div`
  position: absolute;
  overflow: hidden;
`;

// Touch interceptor overlay - captures all touches to toggle MediaInfo.
// z-9997: below KioskOverlay (9998) and InteractiveElementLayer (9999).
export const TouchInterceptor = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9997;
  pointer-events: auto;
  touch-action: manipulation;
`;

export const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 6px;
  pointer-events: none;
  z-index: 10000;
`;

/**
 * Progress bar fill — atualizada a cada `onTimeUpdate` do <video>
 * (~4×/s). Usa {@code .attrs()} pra empurrar a parte dinâmica
 * ({@code width}) como inline style, evitando explosão de classes
 * CSS do styled-components ("Over 200 classes were generated...").
 *
 * <p>O CSS transition continua funcionando — transições aplicam-se
 * independentemente de o valor vir de classe ou de inline style.
 */
/**
 * Countdown discreto no canto inferior direito — usado em modo kiosk
 * locked, quando a {@link ProgressBar} é suprimida pra não chamar atenção
 * do cliente final na loja. Fonte minúscula, opacity baixa: só legível
 * de perto (caso o operador precise verificar progresso da peça).
 */
export const KioskCountdownBadge = styled.div`
  position: absolute;
  right: 3px;
  bottom: 1px;
  font-size: 8px;
  font-family: monospace;
  color: rgba(255, 255, 255, 0.35);
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
  pointer-events: none;
  z-index: 10000;
  user-select: none;
`;

// Barra de progresso — animada via `transform: scaleX()` (compositor/GPU) em vez
// de `width` (Renato 2026-07-05, gargalo A7). O <PlaybackProgressBar> escreve
// `style.transform` a cada rAF. scaleX é composited (sem layout/paint por frame)
// → cada frame fica MUITO mais barato no A7 (menos CPU/thermal) do que animar
// `width` (que dispara layout+paint por frame). `will-change: transform` promove
// a própria layer de composição; `transform-origin: left` cresce da esquerda pra
// direita (igual ao antigo width). NOTA: o gradiente escala junto com o scaleX —
// imperceptível numa barra de 6px de altura.
export const ProgressFill = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform: scaleX(0);
  transform-origin: left center;
  will-change: transform;
  background: linear-gradient(90deg, #4fc3f7 0%, #29b6f6 100%);
  border-bottom-right-radius: 4px;
`;

/**
 * Renato 2026-06-14 — Fase 1: padronizar object-fit em TODOS os
 * tipos de playback (playlist, campanha, trigger). Antes triggers
 * usavam `contain` (barras pretas) e o resto `cover` (corta laterais).
 * Agora é `cover` único pra consistência visual. Fase 2 vai tornar
 * configurável por tenant.
 */
/**
 * v1.0.190 (Fase 2 — feature flag fit mode):
 * `$objectFit` segue o `effectiveMode` do useViewportScale:
 * - `contain` → `object-fit: contain` (letterbox dentro do container)
 * - `cover`   → `object-fit: cover`   (corta dentro do container)
 * - `stretch` → `object-fit: fill`    (mídia estica junto com container deformado)
 *
 * Quando $objectFit não é fornecido (callers legacy), default = `cover` (preserva
 * comportamento Fase 1 v1.0.180).
 */
/**
 * `$hidden` (2026-07-16) — esconde o <video> via `visibility: hidden` enquanto o
 * canvas de freeze cobre a transição (SÓ Tizen/webOS — vide gate no Stream). Em
 * Tizen o HW overlay do vídeo pisca PRETO ao trocar de src e renderiza ACIMA do
 * canvas (overlays de HW ignoram z-index) → ~300ms de preto. Escondendo o <video>
 * enquanto o freeze está visível, o overlay some; revela-se junto com o hide do
 * freeze (1º frame já apresentado via rVFC). `visibility:hidden` (NÃO
 * `display:none`) preserva layout + mantém o decode/playback vivo → o
 * `requestVideoFrameCallback` do 1º frame ainda dispara pra soltar o freeze.
 */
export const VideoPlayer = styled.video<{
  $objectFit?: 'contain' | 'cover' | 'stretch';
  $hidden?: boolean;
}>`
  width: 100%;
  height: 100%;
  object-fit: ${({ $objectFit }) =>
    $objectFit === 'stretch'
      ? 'fill'
      : $objectFit === 'contain'
        ? 'contain'
        : 'cover'};
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  pointer-events: none; /* Allow clicks to pass through to Container */
  z-index: 1;

  /* Hide native WebKit/Android video controls */
  &::-webkit-media-controls,
  &::-webkit-media-controls-panel,
  &::-webkit-media-controls-play-button,
  &::-webkit-media-controls-overlay-play-button,
  &::-webkit-media-controls-start-playback-button,
  &::-webkit-media-controls-enclosure,
  &::-webkit-media-controls-container {
    display: none !important;
    -webkit-appearance: none !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
`;

/**
 * Renato 2026-06-14 — Fase 2 feature flag fit mode (vide VideoPlayer acima).
 */
export const ImagePlayer = styled.img<{
  $objectFit?: 'contain' | 'cover' | 'stretch';
  $overHwPlane?: boolean;
}>`
  width: 100%;
  height: 100%;
  object-fit: ${({ $objectFit }) =>
    $objectFit === 'stretch'
      ? 'fill'
      : $objectFit === 'contain'
        ? 'contain'
        : 'cover'};
  display: block;
  pointer-events: none; /* Allow clicks to pass through to Container */
  /* BACKEND AVPLAY (2026-07-21): o slot de video vira um object (plano HW) position:fixed
     z-index:1 SEMPRE montado (segura o ultimo frame no video->imagem). Uma img ESTATICA
     (default) pinta ABAIXO de qualquer elemento posicionado -> o object cobriria a imagem
     (1o slot preto no boot; imagens "puladas" atras do frame segurado). Sob AVPlay a imagem
     precisa ficar ACIMA do plano HW -> position:FIXED inset:0 z-index:2 (> 1 do object;
     mascaras/QR z-9998+ seguem por cima). Android/browser (sem object) mantem o default
     estatico -- la o hold do video->imagem depende da img ficar POR BAIXO do video segurado.

     RETRATO (2026-07-22): tem que ser position:fixed inset:0 (NAO absolute) — IGUAL ao
     object do plano HW. O absolute posicionava a img no ContentViewport, que em retrato e
     ESCALADO/rotacionado pelo ScaledViewport (transform) -> coordenadas DIFERENTES do plano
     fixed do video -> a img nao cobria o plano inteiro e sobrava uma FAIXA do video
     aparecendo por baixo ("pedaco faltando" da imagem). position:fixed inset:0 ancora a img
     no MESMO viewport fisico do plano -> cobertura exata em retrato e paisagem.

     BACKGROUND OPACO (2026-07-23): o freeze() do AVPlay segura o ULTIMO FRAME do video no
     plano HW (still-mode) durante TODA a imagem — so e limpo quando o proximo VIDEO carrega.
     Um img nao tem background; com object-fit:contain as barras de letterbox ficam
     TRANSPARENTES -> o frame de video congelado atras VAZA na letterbox (faixa visivel).
     background:#000 preenchia a letterbox de preto -> a imagem cobre 100% do plano opaco.
     So no ramo $overHwPlane (Tizen AVPlay): fora dele a img PRECISA ficar transparente pra o
     hold-frame do video->imagem (Android/browser) aparecer por baixo (z-index:1).

     PRETO video->imagem (2026-07-26): a premissa "bitmap+bg pintam no mesmo frame" e FALSA na
     TV consumer — o decode da imagem e ASSINCRONO (centenas de ms a ~1s), entao o background:#000
     pinta ANTES do bitmap => caixa preta opaca (z2) tapa o plano HW (z1) durante o decode => PRETO
     no video->imagem (unica transicao que monta <img> SEM o freeze-canvas cobrindo, pois vem de
     video). Fix: SEM background aqui — durante o decode a img fica TRANSPARENTE e o frame segurado
     do plano HW (still-mode) aparece por baixo; ao pintar, o bitmap cobre. Em stretch/fill (default
     do build AVPlay) a img preenche 100% -> sem letterbox -> sem vazamento. TODO contain:
     reintroduzir o bg em TWO-PHASE (so DEPOIS do 1o paint, via decode()/afterPaint) pra tapar a
     letterbox sem o flash preto. */
  ${({ $overHwPlane }) =>
    $overHwPlane ? 'position: fixed; inset: 0; z-index: 2;' : ''}
`;

export const AudioContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
`;

export const AudioThumbnail = styled.img`
  width: 400px;
  height: 400px;
  object-fit: cover;
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
  margin-bottom: 32px;
`;

export const AudioTitle = styled.h2`
  font-size: 48px;
  font-weight: 600;
  color: #ffffff;
  text-align: center;
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #000000;
  animation: ${fadeIn} 300ms ease-in-out;
  pointer-events: none; /* Allow clicks to pass through to Container */
  z-index: 100;
`;

// v1.0.71 — Preto sólido sem fade-in nem children, usado entre uma mídia
// e outra (Resolvendo mídia / Carregando vídeo / Carregando imagem). Renato
// pediu corte seco e tela 100% preta — sem spinner, sem texto, sem 300ms de
// fade que deixa o frame anterior aparecendo por baixo. Diferente do
// LoadingOverlay (que tem fadeIn + flex pra spinner/texto do BOOT do app).
export const BlackTransition = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #000000;
  pointer-events: none;
  z-index: 100;
`;

// Solid black overlay for transitions - no animation, high z-index
export const TransitionOverlay = styled.div`
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: 100% !important;
  min-width: 100vw !important;
  min-height: 100vh !important;
  background: #000000 !important;
  z-index: 99999 !important;
  pointer-events: none;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const LoadingSpinner = styled.div`
  width: 80px;
  height: 80px;
  border: 4px solid rgba(79, 195, 247, 0.2);
  border-top-color: #4fc3f7;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

export const LoadingText = styled.p`
  font-size: 32px;
  color: #b0b0b0;
  margin-top: 24px;
`;

export const ServerUrlInfo = styled.p`
  font-size: 18px;
  color: #4fc3f7;
  margin-top: 16px;
  font-family: monospace;
  opacity: 0.8;
`;

export const ErrorOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.9);
  pointer-events: none; /* Allow clicks to pass through to Container */
`;

export const ErrorText = styled.p`
  font-size: 32px;
  color: #f44336;
  text-align: center;
  max-width: 600px;
`;

// MuteIndicator removido (Mai/2026) — autoplay com som funciona em Android
// via setMediaPlaybackRequiresUserGesture(false); volume controlado server-side.
// Vide doc no stream.store.ts isMuted.

export const MediaInfo = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 16px;
  left: 16px;
  padding: 16px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 8px;
  font-size: 20px;
  color: #ffffff;
  font-family: 'Roboto Mono', monospace;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 300ms ease-in-out;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  z-index: 10001;

  p {
    margin-bottom: 4px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  span {
    color: #4fc3f7;
  }
`;

// CrashErrorBanner: caixa vermelha de crashes capturados, extraída do
// MediaInfo em 2026-06-23. Fica no canto superior direito com z-index
// MAIOR que o LoopInfoPanel pra garantir que o botão "↑ Limpar" sempre
// fique clicável, mesmo com playlists grandes que esticam o painel de
// campanhas (Renato — não conseguia clicar em Limpar pois o LoopInfoPanel
// cobria a caixa quando ela ficava embutida no MediaInfo à esquerda).
export const CrashErrorBanner = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 10px 12px;
  background-color: rgba(244, 67, 54, 0.95);
  border: 1px solid #e53935;
  border-radius: 6px;
  color: #fff;
  font-family: 'Roboto Mono', monospace;
  font-size: 16px;
  max-width: 40vw;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 300ms ease-in-out;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  z-index: 10003;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);

  p {
    margin: 0;
  }
`;

// LoopInfoPanel: painel destacado do "Loop & Impressões" no canto superior
// direito. Movido pra cá em 2026-06-23 pois ficava muito grande dentro do
// MediaInfo e empurrava o conteúdo pra cima, rolando a tela com kiosk OFF.
export const LoopInfoPanel = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 16px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 8px;
  font-size: 20px;
  color: #ffffff;
  font-family: 'Roboto Mono', monospace;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 300ms ease-in-out;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  z-index: 10001;
  max-width: 40vw;

  p {
    margin-bottom: 4px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  span {
    color: #4fc3f7;
  }
`;

// ===========================================================================
// ORIENTATION SELECTOR STYLES (for MediaInfo overlay)
// ===========================================================================

export const OrientationButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

export const OrientationButton = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 500;
  font-family: 'Roboto', sans-serif;
  cursor: pointer;
  transition: all 150ms ease-in-out;
  background: ${({ $selected }) =>
    $selected
      ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)'
      : 'rgba(255, 255, 255, 0.05)'};
  color: ${({ $selected }) => ($selected ? '#ffffff' : '#b0b0b0')};
  border: 2px solid
    ${({ $selected }) => ($selected ? '#2196f3' : 'rgba(255, 255, 255, 0.1)')};

  &:hover {
    background: ${({ $selected }) =>
      $selected
        ? 'linear-gradient(135deg, #42a5f5 0%, #2196f3 100%)'
        : 'rgba(255, 255, 255, 0.1)'};
    border-color: ${({ $selected }) =>
      $selected ? '#42a5f5' : 'rgba(255, 255, 255, 0.2)'};
  }

  &:focus {
    transform: scale(1.05);
    box-shadow: 0 0 0 4px rgba(79, 195, 247, 0.3);
    outline: none;
  }

  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
  }
`;

export const AutoBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #4fc3f7 !important;
  background: rgba(79, 195, 247, 0.15);
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 4px;
`;

// ===========================================================================
// SPEED CONTROLS (inside MediaInfo overlay)
// ===========================================================================

export const SpeedControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

export const SpeedSliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
`;

export const SpeedSlider = styled.input`
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
    cursor: pointer;
    border: 3px solid #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    transition: transform 150ms ease-in-out;
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.1);
  }

  &::-webkit-slider-thumb:active {
    transform: scale(0.95);
  }

  &::-moz-range-thumb {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
    cursor: pointer;
    border: 3px solid #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  &::-webkit-slider-runnable-track {
    height: 8px;
    border-radius: 4px;
    background: linear-gradient(
      to right,
      rgba(255, 152, 0, 0.6) 0%,
      rgba(255, 152, 0, 0.2) 100%
    );
  }

  &::-moz-range-track {
    height: 8px;
    border-radius: 4px;
    background: linear-gradient(
      to right,
      rgba(255, 152, 0, 0.6) 0%,
      rgba(255, 152, 0, 0.2) 100%
    );
  }
`;

export const SpeedValue = styled.span<{ $active: boolean }>`
  min-width: 48px;
  font-size: 24px;
  font-weight: 700;
  font-family: 'Roboto Mono', monospace;
  color: ${({ $active }) => ($active ? '#ff9800' : '#ffffff')};
  text-align: right;
`;

// ===========================================================================
// OFFLINE BADGE (shown when playing from cache while offline)
// ===========================================================================

export const OfflineBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(255, 152, 0, 0.9);
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1px;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 14px;
    height: 14px;
    fill: currentColor;
  }
`;

export const SpeedIndicator = styled.div<{ $visible: boolean }>`
  position: absolute;
  bottom: 32px;
  left: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 20px;
  background: rgba(255, 152, 0, 0.9);
  border-radius: 8px;
  font-size: 24px;
  font-weight: 700;
  font-family: 'Roboto Mono', monospace;
  color: #ffffff;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 200ms ease-in-out;
  pointer-events: auto;
  cursor: pointer;
  user-select: none;
  z-index: 1000;

  &:hover {
    background: rgba(255, 167, 38, 0.95);
  }

  &:active {
    transform: scale(0.95);
  }
`;

// ===========================================================================
// FULLSCREEN BUTTON (browser/android only)
// ===========================================================================

export const FullscreenButton = styled.button<{ $visible: boolean }>`
  position: absolute;
  bottom: 32px;
  right: 32px;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  cursor: pointer;
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition:
    all 200ms ease,
    opacity 300ms ease-in-out;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(79, 195, 247, 0.5);
  }

  svg {
    width: 24px;
    height: 24px;
    fill: #ffffff;
  }
`;

// TriggerBadge removido (Mai/2026) — em retail media, badge "TRIGGER: foo (modo)"
// no canto superior direito era ruído visual pro cliente final. O conteúdo de
// trigger continua tocando normalmente; o indicador era só pra debug. Caso
// precise voltar pra ambiente de desenvolvimento, ver histórico do git.
